import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Box, CircularProgress, MenuItem,
  Select, Typography
} from "@mui/material";
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, ComposedChart,
  LabelList, Legend, Line, Pie, PieChart, ReferenceLine, ResponsiveContainer,
  Tooltip, XAxis, YAxis
} from "recharts";
import {
  getDashboardCampaigns        as getCampaigns,
  getDashboardTimingHourly     as getTimingData,
  getDashboardOutcomeHourly    as getOutcomeData,
  getDashboardHeatmapHourly    as getHeatmapData,
  getDashboardPtpSummaryHourly as getPtpSummary,
  getDashboardPtpByDayHourly   as getPtpByDay,
  getDashboardAccountsHourly   as getAccounts,
  // Funnel money steps — see paymentsTotals below.
  getDashboardPaymentsSummary  as getPaymentsSummary,
} from "../../services/pbi/pbiservices";
import {
  useClientTimeZone, zonedNow, zoneAbbr,
} from "../../utils/clienttimezone";

// ── Constants ──────────────────────────────────────────────────────
const BLUE     = "#378ADD";
const PINK     = "#D4537E";
const GREEN    = "#1D9E75";
const AMBER    = "#BA7517";
const GRAY     = "#888780";
const OPTOUT_C = "#A32D2D";
// Dotted average rule and the figure beside each chart title. A hue no series
// uses, so the annotation never reads as another measurement.
const AVG_INK  = "#0F6E56";

// ── THE PALETTE IS THE REPORT'S ──
// rpt1's PDF is the artifact people forward, so it is the one that wins: RPC
// blue, PTP green, Kept a darker shade of that green. Accounts takes brown,
// which is what freed blue for RPC.
//
// A metric reads the same hue wherever it appears — funnel bar, KPI figure,
// line, area, table column — on every page and in the emailed PDF. Colour
// identifies rather than decorates.
//
// ── THREE GREENS, AND THAT IS THE POINT ──
//   PTP          mid green    the whole
//   Kept         dark green   the part that arrived
//   Uncommitted  mint         the part that has not
// Kept + Uncommitted = PTP, so one hue at three values says the containment
// the numbers already have. Uncommitted was mint before this and stayed put;
// it only reads as a shade of PTP now that PTP is green.
//
// ⚠ SENT (navy) AND RPC (blue) ARE NOW BOTH BLUE. In the funnel they are
// separated by the brown Accounts step and read fine. In the "Sent vs
// Responses" grouped bar chart further down they sit side by side, which is
// the one place on this page where the two are hard to tell apart. If it
// grates, move C_SENT rather than C_RPC — RPC is the one matching the report.
// Darken (pct < 0) or lighten (pct > 0) a hex colour. Same helper and same
// -0.42 the report uses, so the app and the PDF produce the identical pair.
const shade = (hex, pct) => {
  const n = parseInt(String(hex).slice(1), 16);
  const f = (c) => Math.max(0, Math.min(255, Math.round(c + c * pct)));
  return `#${[(n >> 16) & 255, (n >> 8) & 255, n & 255]
    .map(f).map((v) => v.toString(16).padStart(2, "0")).join("")}`;
};
const KEPT_SHADE = -0.42;   // how much darker the Kept band sits

const C_SENT        = "#0C447C";  // navy
const C_ACCOUNTS    = "#8A5524";  // brown
const C_RPC         = "#378ADD";  // blue
const C_PTP         = "#1D9E75";  // green
const C_SL          = "#D4537E";  // pink
const C_UNCOMMITTED = "#3DBE92";  // mint — the light end of the PTP green
const C_24HR        = "#A01F50";  // deep pink

// Derived, not a literal, so it follows C_PTP wherever the palette goes. Kept
// is CONTAINED in PTP, and a shade of one hue says that where a second hue
// would say "different thing".
//
// ⚠ THIS LANDS AT #115C44, WHICH IS 26 RGB UNITS FROM THE BASELINE INK
// (#0F6E56). On a chart with a dotted average rule the two read as the same
// colour. The report has the same collision; see the note where AVG_INK is
// defined.
const C_KEPT        = shade(C_PTP, KEPT_SHADE);   // dark green

// ── THE DONUT IS THE ONE PLACE KEPT IS NOT NESTED ──
// Its slices are mutually exclusive conversation classifications: a
// conversation is classified Payment OR Promise, never both. Two shades of one
// hue would put two near-identical slices next to each other and say "part of"
// about quantities that are siblings.
//
// So it keeps a distinct hue — the one Kept carried everywhere before the
// funnel and the stacked charts moved to shading. Deliberately NOT C_KEPT:
// this is a different relationship and gets a different encoding.
const C_OUTCOME_PAYMENT = "#7F77DD";  // purple


const OUTCOME_COLORS = {
  Payment:    C_OUTCOME_PAYMENT,
  Promise:    C_PTP,
  SmartLink:  C_SL,
  NoAction:   AMBER,
  NoResponse: GRAY,
};

const OUTCOME_LABELS = { Payment: "Kept", Promise: "PTP", SmartLink: "Smart Links" };

const EXCLUDED_OUTCOMES = new Set([
  "noresponse", "optout", "unsubscribe", "stop",
]);
const normOutcome = (k) => String(k || "").replace(/[^a-z]/gi, "").toLowerCase();

const GRAN_OPTIONS    = ["Day", "Week", "Month"];
const RANGE_OPTIONS = [
  "1D", "2D", "3D",
  "7D", "30D", "90D", "1Y",
  "C Wk", "CW+Last",
  "This Mo", "Cur+Last", "Prev 2Mo", "Prev 3Mo",
];

function toLocalYMD(d) {
  const yy = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const dy = String(d.getDate()).padStart(2, "0");
  return `${yy}-${mo}-${dy}`;
}

// Still resolved client-side even though the API now buckets by client zone:
// the range boundaries ("today", "this week") must be computed in the
// tenant's zone before being sent, or a range picked at 11pm PT would resolve
// against the wrong "today".
function getDateRange(preset, timeZone) {
  const now = zonedNow(timeZone);
  const y   = now.getFullYear();
  const m   = now.getMonth();
  let start, end;

  const dow        = now.getDay();
  const thisMonday = new Date(now);
  thisMonday.setDate(now.getDate() - ((dow + 6) % 7));
  thisMonday.setHours(0, 0, 0, 0);

  const lastMonday = new Date(thisMonday);
  lastMonday.setDate(thisMonday.getDate() - 7);

  switch (preset) {
    case "1D":
      start = new Date(now); start.setDate(now.getDate() - 1);   end = now; break;
    case "2D":
      start = new Date(now); start.setDate(now.getDate() - 2);   end = now; break;
    case "3D":
      start = new Date(now); start.setDate(now.getDate() - 3);   end = now; break;
    case "7D":
      start = new Date(now); start.setDate(now.getDate() - 7);   end = now; break;
    case "30D":
      start = new Date(now); start.setDate(now.getDate() - 30);  end = now; break;
    case "90D":
      start = new Date(now); start.setDate(now.getDate() - 90);  end = now; break;
    case "1Y":
      start = new Date(now); start.setDate(now.getDate() - 365); end = now; break;
    case "C Wk":
      start = thisMonday; end = now; break;
    case "CW+Last":
      start = lastMonday; end = now; break;
    case "This Mo":
      start = new Date(y, m, 1);     end = now; break;
    case "Cur+Last":
      start = new Date(y, m - 1, 1); end = now; break;
    case "Prev 2Mo":
      start = new Date(y, m - 2, 1); end = new Date(y, m, 0); break;
    case "Prev 3Mo":
    default:
      start = new Date(y, m - 3, 1); end = new Date(y, m, 0); break;
  }

  return { startDate: toLocalYMD(start), endDate: toLocalYMD(end) };
}

function getPrevDateRange(startDate, endDate) {
  const parse = (s) => {
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(s);
  };
  const start  = parse(startDate);
  const end    = parse(endDate);
  const durMs  = end.getTime() - start.getTime();
  const dayMs  = 86_400_000;
  const prevEnd   = new Date(start.getTime() - dayMs);
  const prevStart = new Date(prevEnd.getTime() - durMs);
  return { startDate: toLocalYMD(prevStart), endDate: toLocalYMD(prevEnd) };
}

// day_of_week / hour_of_day now arrive from the API already converted to the
// client zone — no client-side shift needed.
const HEATMAP_HOURS       = ["8a","9a","10a","11a","12p","1p","2p","3p","4p","5p","6p","7p","8p","9p","10p"];
const HEATMAP_HOUR_VALUES = [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22];
const HEATMAP_DAYS        = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

// ── Helpers ────────────────────────────────────────────────────────
function safeNum(v) { const n = Number(v); return isNaN(n) ? 0 : n; }

// ── AVERAGE RULE + HEADER FIGURE ───────────────────────────────────
// The two rules the scorecard uses, for the same reasons.
//
// activeMean — mean over buckets WITH ACTIVITY only. A day the system was not
// running is not a zero; counting it drags the rule down and makes every real
// day look above trend.
//
// pooledPct — for a RATE, total numerator over total denominator. NOT the mean
// of the per-bucket percentages, which weights a 40-send day the same as a
// 4,000-send one; on a book whose volume moves the two differ by multiples.
function activeMean(rows, key) {
  const active = (rows || []).filter(r => safeNum(r[key]) > 0);
  return active.length
    ? active.reduce((a, r) => a + safeNum(r[key]), 0) / active.length
    : 0;
}

function pooledPct(rows, numKey, denKey) {
  const den = (rows || []).reduce((a, r) => a + safeNum(r[denKey]), 0);
  return den
    ? ((rows || []).reduce((a, r) => a + safeNum(r[numKey]), 0) * 100) / den
    : 0;
}

function fmt(n, dec = 0) {
  if (n == null || isNaN(n)) return "—";
  return Number(n).toFixed(dec);
}

function fmtPct(v, dec = 2) {
  const n = Number(v);
  if (v == null || isNaN(n)) return "—";
  return `${n.toFixed(dec)}%`;
}

function fmtMins(secs) {
  if (secs == null || isNaN(secs)) return "—";
  return fmt(secs / 60, 2);
}

function fmtDate(val) {
  if (!val) return "";
  const m = typeof val === "string" && val.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const d = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(val);
  if (isNaN(d)) return val;
  return d.toLocaleDateString("en-US", {
    month: "2-digit", day: "2-digit", year: "2-digit",
  });
}

function fmtAxisDate(val, gran = "day") {
  if (!val) return "";
  const m = typeof val === "string" && val.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const d = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(val);
  if (isNaN(d)) return String(val);
  if (String(gran).toLowerCase() === "month")
    return d.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
  const wd = ["Su","Mo","Tu","We","Th","Fr","Sa"][d.getDay()];
  const md = d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit" });
  return `${wd} ${md}`;
}

function buildDelta(cur, prev, { goodWhenUp = true, fmtVal } = {}) {
  const c = Number(cur);
  const p = Number(prev);
  if (!isFinite(c) || !isFinite(p) || p === 0) return null;
  const pct      = ((c - p) / p) * 100;
  const up       = pct > 0;
  const prevText = fmtVal ? fmtVal(p) : p.toLocaleString();
  return {
    up,
    good: goodWhenUp ? up : !up,
    text: `${up ? "+" : ""}${pct.toFixed(1)}% vs ${prevText}`,
  };
}

function heatColor(val, max) {
  if (!val || max === 0) return "#F1EFE8";
  const ratio = val / max;
  if (ratio < 0.2)  return "#E6F1FB";
  if (ratio < 0.4)  return "#B5D4F4";
  if (ratio < 0.65) return "#378ADD";
  return "#0C447C";
}

// ── FUNNEL MONEY STEPS COME FROM THE PAYMENTS ROUTE ────────────────
// PTP, Kept and Smart Links are read from /dashboard/payments-summary, which
// aggregates raw customerconvevent, rather than from summary_ptp_hourly.
//
// The two disagree. The summary table is built by a refresh proc and can lag
// or drift from the events it was built from, and the Payments dashboard
// already reports the raw figures — so a funnel sourced from the summary
// showed a different PTP than the page next to it for the same range. One of
// them had to win, and the one closer to the events is the one to trust.
//
// Sent, Accounts and RPC have no counterpart in that route and still come
// from their own sources.
//
// Falls back to the summary when the payments call returns nothing, so a
// failed fetch degrades to the previous numbers rather than an empty funnel.
function paymentsTotals(rows) {
  const t = (rows || []).reduce((a, r) => ({
    kept:       a.kept       + safeNum(r.payment_count),
    keptAmt:    a.keptAmt    + safeNum(r.payment_total),
    promise:    a.promise    + safeNum(r.promise_count),
    promiseAmt: a.promiseAmt + safeNum(r.promise_total),
    sl:         a.sl         + safeNum(r.smartlink_count),
    slAmt:      a.slAmt      + safeNum(r.smartlink_total),
  }), { kept: 0, keptAmt: 0, promise: 0, promiseAmt: 0, sl: 0, slAmt: 0 });
  return {
    ...t,
    ptp:    t.kept + t.promise,
    ptpAmt: t.keptAmt + t.promiseAmt,
    has:    (rows || []).length > 0,
  };
}

// ── Sub-components ─────────────────────────────────────────────────
function KpiCard({ label, value, color = BLUE, delta }) {
  return (
    <Box sx={{
      background: "var(--mui-palette-background-default, #f7f7f7)",
      borderRadius: 2, p: "10px 14px", minWidth: 0,
    }}>
      <Typography sx={{ fontSize: 11, color: "text.secondary", mb: 0.5 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 22, fontWeight: 500, color, lineHeight: 1.2 }}>
        {value}
      </Typography>
      {delta && (
        <Typography sx={{ fontSize: 11, color: delta.good ? "#3B6D11" : "#A32D2D", mt: 0.5 }}>
          {delta.up ? "▲" : "▼"} {delta.text}
        </Typography>
      )}
    </Box>
  );
}

function SectionTitle({ children }) {
  return (
    <Typography sx={{
      fontSize: 11, fontWeight: 500, color: "text.secondary",
      textTransform: "uppercase", letterSpacing: "0.06em", mb: 1, mt: 2,
    }}>
      {children}
    </Typography>
  );
}

function Caption({ children }) {
  return (
    <Typography sx={{
      fontSize: 11, color: "text.disabled", mt: 0.75, lineHeight: 1.45,
    }}>
      {children}
    </Typography>
  );
}

function ChartCard({ title, right, children, height = 200 }) {
  return (
    <Box sx={{
      border: "0.5px solid", borderColor: "divider",
      borderRadius: 2, p: "12px 14px",
    }}>
      {(title || right) && (
        <Box sx={{ display: "flex", alignItems: "baseline",
                   justifyContent: "space-between", gap: 1, mb: 1 }}>
          <Typography sx={{
            fontSize: 11, fontWeight: 500, color: "text.secondary",
            textTransform: "uppercase", letterSpacing: "0.05em",
          }}>
            {title}
          </Typography>
          {right && (
            <Typography sx={{ fontSize: 11, color: AVG_INK, whiteSpace: "nowrap" }}>
              {right}
            </Typography>
          )}
        </Box>
      )}
      <Box sx={{ width: "100%", height: height === "auto" ? "auto" : height }}>
        {children}
      </Box>
    </Box>
  );
}

function PillGroup({ label, options, value, onChange, groups }) {
  let groupBoundaries = new Set();
  if (groups) {
    let idx = 0;
    groups.forEach((size, gi) => {
      if (gi < groups.length - 1) { idx += size; groupBoundaries.add(idx); }
    });
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Typography sx={{
        fontSize: 11, color: "text.secondary",
        textTransform: "uppercase", letterSpacing: "0.05em",
      }}>
        {label}
      </Typography>
      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", alignItems: "center" }}>
        {options.map((opt, i) => {
          const active = value === opt;
          return (
            <React.Fragment key={opt}>
              {groupBoundaries.has(i) && (
                <Box sx={{ width: "1px", height: 18, bgcolor: "divider", mx: 0.5, flexShrink: 0 }} />
              )}
              <Box
                onClick={() => onChange(opt)}
                sx={{
                  px: "10px", py: "3px", fontSize: 12, borderRadius: "20px",
                  cursor: "pointer", userSelect: "none",
                  border: "0.5px solid",
                  borderColor: active ? BLUE : "divider",
                  background:  active ? BLUE : "transparent",
                  color:       active ? "#fff" : "text.secondary",
                  fontWeight:  active ? 500 : 400,
                  transition:  "all .15s",
                  "&:hover": { borderColor: BLUE },
                }}
              >
                {opt}
              </Box>
            </React.Fragment>
          );
        })}
      </Box>
    </Box>
  );
}

// ── Heatmap ────────────────────────────────────────────────────────
// Cells are already in the CONFIGURED client zone — day_of_week and
// hour_of_day arrive pre-converted from /dashboard/hourly/heatmap, computed
// per row server-side via AT TIME ZONE. No client-side shift, so this is
// DST-exact across the whole range rather than the single-offset
// approximation the old summary_heatmap-backed version used.
//
// texts_responded IS rpc_count from summary_ptp_hourly, so this grid's grand
// total equals the RPC KPI tile exactly — provided the route adds opt-outs
// back on both sides. If only the summary route is changed and the heatmap
// route is not, this grid will read LOW against the tile by the opt-out count.
function Heatmap({ data }) {
  const grid = useMemo(() => {
    const map = {};
    (data || []).forEach(r => {
      const dow = Number(r.day_of_week);
      const h   = Number(r.hour_of_day);
      if (!dow || isNaN(h)) return;
      const key = `${dow}_${h}`;
      map[key] = (map[key] || 0) + (Number(r.texts_responded) || 0);
    });
    return map;
  }, [data]);

  const max = Math.max(1, ...Object.values(grid));

  const rowTotals = HEATMAP_HOUR_VALUES.map(hour =>
    HEATMAP_DAYS.reduce((sum, _, di) => sum + (grid[`${di + 1}_${hour}`] || 0), 0)
  );
  const colTotals = HEATMAP_DAYS.map((_, di) =>
    HEATMAP_HOUR_VALUES.reduce((sum, hour) => sum + (grid[`${di + 1}_${hour}`] || 0), 0)
  );
  const grandTotal = colTotals.reduce((a, b) => a + b, 0);

  const totalCellSx = {
    height: 22, borderRadius: "2px",
    background: `${BLUE}18`,
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 10, fontWeight: 600, color: BLUE,
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{
        display: "grid",
        gridTemplateColumns: `48px repeat(7, 1fr) 56px`,
        gap: "3px", fontSize: 10,
      }}>
        <Box />
        {HEATMAP_DAYS.map(d => (
          <Box key={d} sx={{ textAlign: "center", color: "text.secondary", pb: 0.5, fontWeight: 500 }}>
            {d}
          </Box>
        ))}
        <Box sx={{ textAlign: "center", color: BLUE, pb: 0.5, fontWeight: 600 }}>Total</Box>

        {HEATMAP_HOURS.map((h, hi) => (
          <React.Fragment key={h}>
            <Box sx={{ textAlign: "right", pr: 1, color: "text.secondary",
              lineHeight: "22px", height: 22 }}>
              {h}
            </Box>
            {HEATMAP_DAYS.map((_, di) => {
              const hour = HEATMAP_HOUR_VALUES[hi];
              const val  = grid[`${di + 1}_${hour}`] || 0;
              return (
                <Box key={di} sx={{
                  height: 22, borderRadius: "2px", background: heatColor(val, max),
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 10, color: val > max * 0.6 ? "#fff" : "text.secondary",
                }}>
                  {val || ""}
                </Box>
              );
            })}
            <Box sx={totalCellSx}>{rowTotals[hi] || ""}</Box>
          </React.Fragment>
        ))}

        <Box sx={{ textAlign: "right", pr: 1, color: BLUE, fontWeight: 600,
          lineHeight: "22px", height: 22 }}>
          Total
        </Box>
        {colTotals.map((v, di) => (
          <Box key={di} sx={totalCellSx}>{v || ""}</Box>
        ))}
        <Box sx={{ ...totalCellSx, background: `${BLUE}33`, color: BLUE, fontWeight: 700 }}>
          {grandTotal || ""}
        </Box>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: "4px",
        mt: 1, fontSize: 10, color: "text.secondary" }}>
        <span>Low</span>
        {["#E6F1FB","#B5D4F4","#378ADD","#0C447C"].map(c => (
          <Box key={c} sx={{ width: 14, height: 10, background: c, borderRadius: "2px" }} />
        ))}
        <span>High</span>
      </Box>
    </Box>
  );
}

// ── Funnel ─────────────────────────────────────────────────────────
// Each step past the first carries its own denominator (`den`) for the
// in-bar percentage, so a step can be read against whatever base makes
// sense for it: RPC against Accounts, PTP and Smart Links against RPC,
// Kept against PTP. The share of Sent drops to the small line under the
// label. Steps with no `den` (Accounts itself) keep % of Sent in the bar.
//
// Labels are display-only renames — the underlying fields are unchanged.
// "RPC" is rpc_count; "Kept" is processed_ptp_count.
// `accounts` comes from its own endpoint (/dashboard/hourly/accounts), not
// from the summary row — the distinct scan is too costly to ride along on
// every summary read. The step is ALWAYS rendered, including at 0, so a
// missing or failed fetch is visible as an empty bar rather than silently
// dropping a stage from the funnel. At 0 its `den` is falsy, so RPC falls
// back to % of Sent.
function Funnel({ summary, channelParam, accounts = 0, pay }) {
  const sent      = safeNum(summary?.texts_sent);
  // RPC is GROSS of opt-outs — a STOP is still a reply. rpc_count from the
  // route is net, so opt-outs are added back here. Opt-outs remain a tile of
  // their own, now a subset of RPC rather than a sibling.
  const responded = safeNum(summary?.rpc_count) + safeNum(summary?.optout_count);
  // Money steps from the payments route; summary is the fallback.
  const kept      = pay?.has ? pay.kept : safeNum(summary?.processed_ptp_count);
  const ptp       = pay?.has ? pay.ptp
                             : safeNum(summary?.processed_ptp_count) + safeNum(summary?.promise_count);
  const smartlink = pay?.has ? pay.sl   : safeNum(summary?.smartlink_count);

  const steps = [
    { label: channelParam === "emails" ? "Emails sent" : "Texts sent", val: sent, color: C_SENT },
    { label: "Accounts",   val: accounts,                              color: C_ACCOUNTS },
    { label: "RPC",        val: responded,                             color: C_RPC, den: accounts },
    { label: "PTP",        val: ptp,                                   color: C_PTP, den: responded },
    { label: "Same Day",       val: kept,                                  color: C_KEPT, den: ptp },
    { label: "Smart Links", val: smartlink,                             color: C_SL,  den: responded },
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, py: 0.5 }}>
      {steps.map((s, i) => {
        const pctOfSent = sent > 0 ? (s.val / sent) * 100 : 0;
        const denPct    = s.den > 0 ? (s.val / s.den) * 100 : null;
        const isFirst   = i === 0;
        // The fill is the number printed inside the bar, 1:1 — no
        // magnification. A step with its own `den` fills to its share of
        // that base; the rest fill to their share of Sent. Floored at 2%
        // so a near-zero step still shows a sliver of color.
        const shownPct  = isFirst ? 100 : (denPct != null ? denPct : pctOfSent);
        const fillW     = shownPct > 0 ? Math.min(100, Math.max(shownPct, 2)) : 0;
        return (
          <Box key={s.label} sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1.5 }}>
            <Box sx={{ flex: 1, minWidth: 0, display: "flex", justifyContent: "center" }}>
              <Box sx={{
                position: "relative", width: "100%", minWidth: 0,
                height: 46, background: "#f0f0f0", borderRadius: "4px",
                transition: "width .4s ease",
              }}>
                <Box sx={{
                  position: "absolute", left: 0, top: 0, bottom: 0,
                  width: `${fillW}%`, background: s.color,
                  borderRadius: isFirst ? "4px" : "4px 0 0 4px",
                }} />
                {(
                  <Box sx={{
                    position: "absolute", inset: 0,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 12, fontWeight: 600, color: isFirst ? "#fff" : "#333",
                    pointerEvents: "none", whiteSpace: "nowrap",
                  }}>
                    {isFirst
                      ? Number(s.val).toLocaleString()
                      : denPct != null
                        ? `${denPct.toFixed(1)}%`
                        : `${pctOfSent.toFixed(1)}%`}
                  </Box>
                )}
              </Box>
            </Box>

            <Box sx={{
              width: 90, flexShrink: 0, textAlign: "left",
              fontSize: 12, color: "text.secondary", lineHeight: 1.35,
            }}>
              <Box sx={{ color: s.color, fontWeight: 600 }}>{s.label}</Box>
              <Box sx={{ mt: 0.25, whiteSpace: "nowrap" }}>
                {!isFirst && <span style={{ fontWeight: 500 }}>{Number(s.val).toLocaleString()}</span>}
              </Box>
              {denPct != null && (
                <Box sx={{ mt: 0.25, fontSize: 10, color: "text.disabled", whiteSpace: "nowrap" }}>
                  {pctOfSent.toFixed(1)}% of Sent
                </Box>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

// ── Aggregation helpers ────────────────────────────────────────────
// The hourly timing endpoint already returns the true per-period mean
// (SUM(total_first_response_secs)/SUM(conv_count), computed server-side), so
// combining multiple periods into one summary number is now a straight
// weighted sum of totals — no need to reconstruct a weight from the response
// histogram, which is what the daily-table version had to do because the old
// route returned AVG(avg_first_response_secs) with no totals to fall back on.
function aggregateTiming(rows) {
  const r = (rows || []).filter(x => x.outcome === "All");
  if (!r.length) return { avgFirstResp: null, maxFirstResp: null, minFirstResp: null, avgConv: null };

  const totalFirstRespSecs = r.reduce((a, b) => a + (Number(b.total_first_response_secs) || 0), 0);
  const totalConvSecs      = r.reduce((a, b) => a + (Number(b.total_conversation_secs)   || 0), 0);
  const totalConvCount     = r.reduce((a, b) => a + (Number(b.conv_count)                || 0), 0);

  const maxFirstResp = Math.max(...r.map(x => x.max_first_response_secs || 0));
  const minVals = r.map(x => x.min_first_response_secs)
                   .filter(v => v != null && v !== "NA" && Number(v) > 0)
                   .map(Number);
  const minFirstResp = minVals.length ? Math.min(...minVals) : null;

  return {
    avgFirstResp: totalConvCount > 0 ? totalFirstRespSecs / totalConvCount : null,
    maxFirstResp,
    minFirstResp,
    avgConv: totalConvCount > 0 ? totalConvSecs / totalConvCount : null,
  };
}

// ── Main Dashboard ─────────────────────────────────────────────────
export default function TrendsDashboard({ channel }) {
  // Day / This Mo on open: a daily series over the current month is the view
  // people land on this page for. Week/90D smoothed away the movement the page
  // exists to show, and had every visit start with two clicks.
  //
  // "This Mo" rather than the rolling "30D" it replaced, so the page agrees
  // with the MTD tab and the emailed report about which days it is describing.
  // A rolling window quietly answers a different question every morning —
  // "the last 30 days" on the 9th reaches back into the previous month, and a
  // figure read off it cannot be checked against anything month-shaped.
  //
  // ⚠ IT IS SHORT AT THE START OF A MONTH. On the 3rd the series is three
  // points; on the 1st it is one. That is honest — there genuinely is only
  // one day of this month — but it is a thinner page than 30D ever gave, and
  // the fixed-window pills are one click away when someone wants depth.
  const [gran,        setGran]        = useState("Day");
  const [rangePreset, setRangePreset] = useState("This Mo");
  const [campaign,    setCampaign]    = useState("All");
  const [campaigns,   setCampaigns]   = useState(["All"]);

  // Sent / Responded / Opt-outs / No response all come from ONE source now —
  // summary_ptp_hourly, via getPtpSummary — so this page, the PTP page, and
  // the Payments funnel all report the same population. There is no longer a
  // separate opt-outs fetch: optout_count and no_response arrive on the same
  // row as texts_sent and rpc_count, computed in the same query, so the
  // identity Sent = RPC + No response holds exactly instead of approximately
  // at range edges. RPC is GROSS of opt-outs (see the route), so Opt-outs is
  // a subset of RPC rather than a third partition.
  const [summary,       setSummary]       = useState({});
  const [byDayRaw,      setByDayRaw]      = useState([]);
  const [timing,        setTiming]        = useState([]);
  const [outcomes,      setOutcomes]      = useState([]);
  const [heatmap,       setHeatmap]       = useState([]);

  const [accounts,    setAccounts]    = useState(0);
  const [paySummary,  setPaySummary]  = useState([]);

  const [prevSummary, setPrevSummary] = useState({});
  const [prevTiming,  setPrevTiming]  = useState([]);

  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState("");

  const { timeZone, tzReady } = useClientTimeZone();

  const channelParam = (channel || "texts").toLowerCase();

  useEffect(() => {
    getCampaigns()
      .then(data => setCampaigns(["All", ...(data || []).map(c => c.name)]))
      .catch(() => {});
  }, []);

  const params = useMemo(() => ({
    ...getDateRange(rangePreset, timeZone),
    campaign:    campaign === "All" ? undefined : campaign,
    granularity: gran.toLowerCase(),
    channel:     channelParam,
  }), [rangePreset, campaign, gran, channelParam, timeZone]);

  const prevParams = useMemo(() => ({
    ...getPrevDateRange(params.startDate, params.endDate),
    campaign:    campaign === "All" ? undefined : campaign,
    granularity: gran.toLowerCase(),
    channel:     channelParam,
  }), [params.startDate, params.endDate, campaign, gran, channelParam]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      // Accounts is fetched for the CURRENT period only — it feeds one funnel
      // step and no delta, so a prev-period distinct scan would be wasted.
      const [s, day, ti, o, h, acc, pay, ps, pti] = await Promise.all([
        getPtpSummary(params),
        getPtpByDay(params),
        getTimingData(params),
        getOutcomeData(params),
        getHeatmapData(params),
        getAccounts(params).catch(() => ({})),
        getPaymentsSummary(params).catch(() => []),
        getPtpSummary(prevParams).catch(() => ({})),
        getTimingData(prevParams).catch(() => []),
      ]);
      setSummary(s      || {});
      setByDayRaw(day   || []);
      setTiming(ti      || []);
      setOutcomes(o     || []);
      setHeatmap(h      || []);
      setAccounts(safeNum(acc?.account_count));
      setPaySummary(pay || []);
      setPrevSummary(ps || {});
      setPrevTiming(pti || []);
    } catch (e) {
      setError("Failed to load dashboard data");
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [params, prevParams]);

  // Gated on tzReady — firing before the client config lands would request a
  // UTC-derived window and then immediately refetch with the real zone.
  useEffect(() => { if (tzReady) loadAll(); }, [loadAll, tzReady]);

  // All five tiles read the single ptp-summary row. RPC is gross of opt-outs,
  // so Sent = RPC + No response; the Opt-outs tile is a subset of RPC shown
  // for its own sake. no_response is returned by the server from the same
  // query, not derived client-side from two different sources.
  const textKpis = useMemo(() => {
    const sent      = safeNum(summary.texts_sent);
    const optouts   = safeNum(summary.optout_count);
    // Gross of opt-outs. no_response from the server is already
    // sent - rpc - optout, i.e. sent - rpcGross, so Sent = RPC + No response
    // still closes exactly.
    const responded = safeNum(summary.rpc_count) + optouts;
    const noResp    = safeNum(summary.no_response);
    const rpcPct    = sent > 0 ? (responded / sent) * 100 : 0;

    const prevSent      = safeNum(prevSummary.texts_sent);
    const prevOptoutCnt = safeNum(prevSummary.optout_count);
    const prevResponded = safeNum(prevSummary.rpc_count) + prevOptoutCnt;
    const prevNoResp    = safeNum(prevSummary.no_response);
    const prevRpcPct    = prevSent > 0 ? (prevResponded / prevSent) * 100 : 0;

    const optoutPct     = sent     > 0 ? (optouts       / sent)     * 100 : 0;
    const prevOptoutPct = prevSent > 0 ? (prevOptoutCnt / prevSent) * 100 : 0;

    return {
      sent:        sent.toLocaleString(),
      responded:   responded.toLocaleString(),
      noResponse:  noResp.toLocaleString(),
      responsePct: sent > 0 ? fmtPct(rpcPct) : "—",
      optouts:     optouts.toLocaleString(),
      sentDelta:        buildDelta(sent,      prevSent,      { goodWhenUp: true  }),
      respondedDelta:   buildDelta(responded, prevResponded, { goodWhenUp: true  }),
      noResponseDelta:  buildDelta(noResp,    prevNoResp,    { goodWhenUp: false }),
      responsePctDelta: buildDelta(rpcPct, prevRpcPct, {
        goodWhenUp: true,
        fmtVal: v => fmtPct(v),
      }),
      optoutDelta:      buildDelta(optoutPct, prevOptoutPct, {
        goodWhenUp: false,
        fmtVal: v => fmtPct(v),
      }),
    };
  }, [summary, prevSummary]);

  const timingKpis = useMemo(() => {
    const cur  = aggregateTiming(timing);
    const prev = aggregateTiming(prevTiming);
    const minOpts = { goodWhenUp: false, fmtVal: v => fmtMins(v) };
    return {
      avgFirstResp: fmtMins(cur.avgFirstResp),
      maxFirstResp: fmtMins(cur.maxFirstResp),
      minFirstResp: fmtMins(cur.minFirstResp),
      avgConv:      fmtMins(cur.avgConv),
      avgFirstRespDelta: buildDelta(cur.avgFirstResp, prev.avgFirstResp, minOpts),
      maxFirstRespDelta: buildDelta(cur.maxFirstResp, prev.maxFirstResp, minOpts),
      minFirstRespDelta: buildDelta(cur.minFirstResp, prev.minFirstResp, minOpts),
      avgConvDelta:      buildDelta(cur.avgConv,      prev.avgConv,      minOpts),
    };
  }, [timing, prevTiming]);

  // Collapsed from (date × DPD bucket) to one row per date. rpc_count is
  // gross of opt-outs, matching the tiles, so the series and the tiles use
  // one definition throughout.
  const textsChartData = useMemo(() => {
    const byDate = new Map();
    (byDayRaw || []).forEach(r => {
      const d = r.summary_date;
      if (!byDate.has(d)) byDate.set(d, { date: d, sent: 0, responded: 0 });
      const a = byDate.get(d);
      a.sent      += safeNum(r.texts_sent);
      a.responded += safeNum(r.rpc_count) + safeNum(r.optout_count);
    });
    return Array.from(byDate.values())
      .sort((a, b) => String(a.date).localeCompare(String(b.date)))
      .map(r => ({
        date:      fmtAxisDate(r.date, gran),
        sent:      r.sent,
        responded: r.responded,
        pct:       r.sent > 0 ? Math.round((r.responded / r.sent) * 100) : 0,
      }));
  }, [byDayRaw, gran]);

  // Sorted before mapping — an unsorted series would make the line zigzag.
  const durationData = useMemo(() =>
    timing
      .filter(r => r.outcome === "All")
      .sort((a, b) => String(a.summary_date).localeCompare(String(b.summary_date)))
      .map(r => ({
        date: fmtAxisDate(r.summary_date, gran),
        // avg_conversation_secs is the true per-period mean now — computed
        // server-side as SUM(total)/SUM(count) within this date bucket, not
        // an average-of-averages.
        avg:  r.avg_conversation_secs ? Math.round(r.avg_conversation_secs / 60 * 100) / 100 : 0,
      }))
  , [timing, gran]);

  const histogramData = useMemo(() => {
    const rows = timing.filter(r => r.outcome === "All");
    return [
      { label: "<1m",    val: rows.reduce((a,b) => a + (b.bucket_under_60s||0), 0) },
      { label: "1-5m",   val: rows.reduce((a,b) => a + (b.bucket_1_5m    ||0), 0) },
      { label: "5-15m",  val: rows.reduce((a,b) => a + (b.bucket_5_15m   ||0), 0) },
      { label: "15-30m", val: rows.reduce((a,b) => a + (b.bucket_15_30m  ||0), 0) },
      { label: "30-60m", val: rows.reduce((a,b) => a + (b.bucket_30_60m  ||0), 0) },
      { label: ">60m",   val: rows.reduce((a,b) => a + (b.bucket_over_60m||0), 0) },
    ];
  }, [timing]);

  // Donut stays on summary_outcome_hourly — a conversation-classification
  // lens, not a transaction lens, so its counts still run above the funnel's
  // PNM-confirmed numbers. Only the bucketing moved to the client zone; the
  // definition, and the gap versus the funnel, are unchanged.
  const outcomeDonut = useMemo(() => {
    const map = {};
    outcomes.forEach(r => {
      if (EXCLUDED_OUTCOMES.has(normOutcome(r.outcome))) return;
      if (!map[r.outcome]) map[r.outcome] = 0;
      map[r.outcome] += r.conv_count;
    });
    return Object.entries(map).map(([name, value]) => ({ name, value }));
  }, [outcomes]);

  const ch           = channel === "Emails" ? "Emails" : "Texts";
  const axisStyle    = { fontSize: 10, fill: "#888" };
  const tooltipStyle = { fontSize: 12 };
  const optoutCount  = safeNum(summary.optout_count);

  // Averages behind each chart's dotted rule. Rates are pooled; counts and
  // durations are the mean of the buckets that had activity.
  const avgRpcPct   = pooledPct(textsChartData, "responded", "sent");
  const avgSent     = activeMean(textsChartData, "sent");
  const avgDuration = activeMean(durationData, "avg");
  const tzLabel      = zoneAbbr(timeZone);
  const payTotals    = paymentsTotals(paySummary);
  // ── THE RANGE THE PILLS RESOLVED TO ──
  // Appended to every chart title and the funnel, the way the scorecard does
  // it. The pills say "90D"; only this says which 90 days, and a screenshot
  // or a PDF export carries no pill state at all.
  const rangeLabel = `${fmtDate(params.startDate)} – ${fmtDate(params.endDate)}`;
  const titleOf    = (t) => (rangeLabel ? `${t} · ${rangeLabel}` : t);



  return (
    <Box sx={{ p: 2 }}>

      {/* ── Slicers ── */}
      <Box sx={{
        display: "flex", flexWrap: "wrap", gap: 2,
        alignItems: "flex-end",
        pb: 2, mb: 1, borderBottom: "0.5px solid", borderColor: "divider",
      }}>
        <PillGroup
          label="Granularity"
          options={GRAN_OPTIONS}
          value={gran}
          onChange={setGran}
        />
        <PillGroup
          label="Date range"
          options={RANGE_OPTIONS}
          value={rangePreset}
          onChange={setRangePreset}
          groups={[3, 4, 2, 4]}
        />
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, alignSelf: "flex-end" }}>
          <Typography sx={{
            fontSize: 11, color: "text.secondary",
            textTransform: "uppercase", letterSpacing: "0.05em",
          }}>
            Campaign
          </Typography>
          <Select
            value={campaign}
            onChange={e => setCampaign(e.target.value)}
            size="small" variant="outlined"
            sx={{
              fontSize: 12, borderRadius: "20px", minWidth: 120, height: 28,
              ".MuiOutlinedInput-notchedOutline": { borderColor: "divider", borderWidth: "0.5px" },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BLUE },
              ".MuiSelect-select": { py: "3px", px: "10px", fontSize: 12, color: "text.secondary" },
              ".MuiSvgIcon-root": { fontSize: 16, color: "text.secondary" },
            }}
          >
            {campaigns.map(c => (
              <MenuItem key={c} value={c} sx={{ fontSize: 12 }}>{c}</MenuItem>
            ))}
          </Select>
        </Box>

        {/* Zone the dates on this page resolve against */}
        <Box sx={{ ml: "auto", alignSelf: "flex-end", fontSize: 11, color: "text.secondary" }}>
          Times shown in {tzLabel}
        </Box>
      </Box>

      {error   && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && (
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", p: 2 }}>
          <CircularProgress size={20} /> Loading dashboard…
        </Box>
      )}

      {!loading && (
        <>
          {/* ── TEXTS / EMAILS ────────────────────────────────── */}
          <SectionTitle>{ch}</SectionTitle>

          {/* Five tiles: the three counts partition Sent exactly
              (RPC + No response, with Opt-outs a subset of RPC), then the
              rate — all from the same query, so the partition is exact
              rather than approximate at range edges. */}
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(0,1fr))", gap: 1, mb: 1.5 }}>
            <KpiCard label={`${ch} sent`} value={textKpis.sent}        color={C_SENT}     delta={textKpis.sentDelta} />
            <KpiCard label="Responses"    value={textKpis.responded}   color={C_RPC}     delta={textKpis.respondedDelta} />
            <KpiCard label="Opt-outs"     value={textKpis.optouts}     color={OPTOUT_C} delta={textKpis.optoutDelta} />
            <KpiCard label="No response"  value={textKpis.noResponse}  color={GRAY}     delta={textKpis.noResponseDelta} />
            <KpiCard label="Responses %"  value={textKpis.responsePct} color={C_RPC}     delta={textKpis.responsePctDelta} />
          </Box>

          {/* Sent over time + conversation funnel.
              VOLUME LEADS. It is the section's first KPI tile made into a
              series, and the funnel beside it opens on the same figure, so the
              row reads as one statement of how much went out and what became
              of it. The rate follows below, against a base the eye has already
              seen — where previously the rate appeared here AND again in the
              combined chart two blocks down, with the volume wedged between
              its two showings. */}
          <Box sx={{ display: "grid", gridTemplateColumns: "3fr 1fr", gap: 1.5, mb: 0.5 }}>
            <ChartCard title={titleOf(`${ch} sent`)} right={`avg ${Math.round(avgSent).toLocaleString()}`} height={300}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={textsChartData} margin={{ top: 18, right: 8, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="sentGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor={C_SENT} stopOpacity={0.15} />
                      <stop offset="95%" stopColor={C_SENT} stopOpacity={0}    />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                  <XAxis dataKey="date" tick={axisStyle} />
                  <YAxis tick={axisStyle} tickFormatter={v => Number(v).toLocaleString()} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => [Number(v).toLocaleString(), n]} />
                  <ReferenceLine y={avgSent} stroke={AVG_INK} strokeDasharray="3 3" strokeWidth={1} />
                  <Area type="monotone" dataKey="sent" name="Sent"
                    stroke={C_SENT} fill="url(#sentGrad)" strokeWidth={2} dot={{ r: 3 }}>
                    <LabelList dataKey="sent" position="top" offset={8}
                      fontSize={10} fill={C_SENT} formatter={v => Number(v).toLocaleString()} />
                  </Area>
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title={titleOf("Conversation funnel")} height="auto">
              <Funnel summary={summary} channelParam={channelParam} accounts={accounts} pay={payTotals} />
            </ChartCard>
          </Box>

          {/* Response rate over time, full width under the pair above.
              `pct` is rounded to a whole number upstream in textsChartData, so
              the point labels stay short at Day granularity over a long range
              — which is what lets this sit at 220 rather than needing the
              taller box the sent series has. */}
          <Box sx={{ mb: 1.5 }}>
            <ChartCard title={titleOf("Responses %")} right={`avg ${fmtPct(avgRpcPct, 1)}`} height={220}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={textsChartData} margin={{ top: 18, right: 8, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="respPctGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor={C_RPC} stopOpacity={0.15} />
                      <stop offset="95%" stopColor={C_RPC} stopOpacity={0}    />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                  <XAxis dataKey="date" tick={axisStyle} />
                  <YAxis tick={axisStyle} tickFormatter={v => `${v}%`} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => [`${v}%`, n]} />
                  <ReferenceLine y={avgRpcPct} stroke={AVG_INK} strokeDasharray="3 3" strokeWidth={1} />
                  <Area type="monotone" dataKey="pct" name="Responses %"
                    stroke={C_RPC} fill="url(#respPctGrad)" strokeWidth={2} dot={{ r: 3 }}>
                    <LabelList dataKey="pct" position="top" offset={8}
                      fontSize={10} fill={C_RPC} formatter={v => `${v}%`} />
                  </Area>
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>
          </Box>

          {/* Sent vs responses */}
          <Box sx={{ mb: 1.5 }}>
            <ChartCard title={titleOf(`${ch} sent vs Responses`)} right={`avg Responses ${fmtPct(avgRpcPct, 1)}`} height={300}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={textsChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                  <XAxis dataKey="date" tick={axisStyle} />
                  <YAxis yAxisId="left"  tick={axisStyle} />
                  <YAxis yAxisId="right" orientation="right"
                    tick={{ ...axisStyle, fill: C_RPC }}
                    tickFormatter={v => `${v}%`} domain={[0, 100]} />
                  <Tooltip contentStyle={tooltipStyle} />
                  {/* On the RIGHT axis: the rule annotates the rate line, not the bars. */}
                  <ReferenceLine yAxisId="right" y={avgRpcPct} stroke={AVG_INK}
                    strokeDasharray="3 3" strokeWidth={1} />
                  <Bar yAxisId="left" dataKey="sent"      name="Sent"      fill={C_SENT} radius={[3,3,0,0]} />
                  <Bar yAxisId="left" dataKey="responded" name="Responses" fill={C_RPC}  radius={[3,3,0,0]} />
                  <Line yAxisId="right" type="monotone" dataKey="pct"
                    name="Responses %" stroke={C_RPC} dot={{ r: 3 }} strokeWidth={2} />
                </ComposedChart>
              </ResponsiveContainer>
            </ChartCard>
          </Box>

          {/* ── Methodology note ── */}
          <Box sx={{
            mb: 1.5, p: "10px 14px", borderRadius: 2,
            border: "0.5px solid", borderColor: `${AMBER}55`,
            background: `${AMBER}0F`, fontSize: 12, color: "text.secondary", lineHeight: 1.5,
          }}>
            <b style={{ color: AMBER }}>Note:</b>{" "}
            <b>Responses</b> are the customers who responded to the texts, <b>including</b>
            opt-outs — a STOP is still a reply. Opt-outs are shown separately above as a
            subset of Responses, not a sibling of them, so Sent = Responses + No response.
            The funnel names this step <b>RPC</b>; it is the same figure.{" "}
            <b>Kept</b> are immediate payments processed through PNM, and{" "}
            <b>PTP</b> is Kept plus the future payments scheduled through PNM. Every figure
            comes from one query, bucketed in {tzLabel}.
          </Box>

          {/* ── HEATMAP — full-width. Now DST-exact and reads from the same
              rpc_count as the RPC tile — but NET of opt-outs, while the tile
              is now gross, so the grid reads LOW against it by the opt-out
              count. Fixing that needs optout_count on the heatmap route. ── */}
          <Box sx={{ mb: 1.5 }}>
            <ChartCard
              title={titleOf(`Responses heatmap — day × hour, ${tzLabel}`)}
              height="auto"
            >
              <Heatmap data={heatmap} />
            </ChartCard>
            <Caption>
              Hours are in {tzLabel}. This grid counts repliers net of opt-outs, so its
              total runs below the Responses tile — which is gross of them — by the opt-out
              count.
            </Caption>
          </Box>

          <Box sx={{ mb: 1.5 }}>
            <ChartCard title={titleOf("Conversation outcomes (classified)")} height={280}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={outcomeDonut} cx="50%" cy="50%"
                    innerRadius="55%" outerRadius="75%"
                    paddingAngle={3} dataKey="value" nameKey="name">
                    {outcomeDonut.map(entry => (
                      <Cell key={entry.name} fill={OUTCOME_COLORS[entry.name] || GRAY} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} formatter={(value, name) => [value, OUTCOME_LABELS[name] || name]} />
                  <Legend iconSize={10} iconType="square" wrapperStyle={{ fontSize: 11 }} formatter={(v) => OUTCOME_LABELS[v] || v} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
            <Caption>
              Non-responses and opt-outs are excluded. Counts are how each conversation was
              classified, so they read higher than the funnel, which counts PNM-confirmed
              transactions. "PTP" here is the promise classification alone, not the funnel's
              Kept + promises.
            </Caption>
          </Box>

          {/* ── DURATION ──────────────────────────────────────── */}
          <SectionTitle>Duration</SectionTitle>

          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0,1fr))", gap: 1, mb: 1.5 }}>
            <KpiCard label="Avg first response (mins)" value={timingKpis.avgFirstResp} color={BLUE}  delta={timingKpis.avgFirstRespDelta} />
            <KpiCard label="Max first response (mins)" value={timingKpis.maxFirstResp} color={BLUE}  delta={timingKpis.maxFirstRespDelta} />
            <KpiCard label="Min first response (mins)" value={timingKpis.minFirstResp} color={BLUE}  delta={timingKpis.minFirstRespDelta} />
            <KpiCard label="Avg conversation (mins)"   value={timingKpis.avgConv}      color={AMBER} delta={timingKpis.avgConvDelta} />
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5, mb: 1.5 }}>
            <ChartCard title={titleOf("Avg conversation duration")} right={`avg ${avgDuration.toFixed(2)}m`} height={180}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={durationData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                  <XAxis dataKey="date" tick={axisStyle} />
                  <YAxis tick={axisStyle} tickFormatter={v => `${v}m`} />
                  <Tooltip contentStyle={tooltipStyle} formatter={v => [`${v} mins`, "Avg duration"]} />
                  <ReferenceLine y={avgDuration} stroke={AVG_INK} strokeDasharray="3 3" strokeWidth={1} />
                  <Line type="monotone" dataKey="avg" name="Avg (mins)"
                    stroke={BLUE} strokeWidth={2} dot={{ r: 3 }}
                    fill="rgba(55,138,221,0.08)" />
                </ComposedChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title={titleOf("Response time distribution")} height={180}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={histogramData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                  <XAxis dataKey="label" tick={axisStyle} />
                  <YAxis tick={axisStyle} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Bar dataKey="val" name="Conversations" radius={[3,3,0,0]}>
                    {histogramData.map((_, i) => (
                      <Cell key={i} fill={["#0C447C","#185FA5","#378ADD","#85B7EB","#B5D4F4","#E6F1FB"][i]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Box>
          <Caption>
            Duration figures are bucketed in {tzLabel} and reflect the true average across the
            selected range, weighted by conversation volume.
          </Caption>
        </>
      )}
    </Box>
  );
}